import React, { CSSProperties } from 'react'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import MUIDialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button, { ButtonProps } from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Typography from '@material-ui/core/Typography'

import { useStyles } from './styles'

interface DialogProps {
  isOpen: boolean
  title: string
  subtitle?: string
  contentStyle?: CSSProperties
  style?: CSSProperties
  className?: string
  contentClassName?: string
  showActions?: boolean
  okButtonText?: string
  okButtonProps?: ButtonProps
  cancelButtonText?: string
  onAccept?: () => void
  onCancel: () => void
  isLoading?: boolean
  overflow?: boolean
  readOnly?: boolean
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onCancel,
  title,
  subtitle,
  children,
  showActions = false,
  okButtonText,
  okButtonProps,
  cancelButtonText,
  onAccept = () => undefined,
  isLoading,
  contentStyle,
  style,
  className,
  contentClassName,
  overflow,
  readOnly,
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()
  const paperClassName = overflow ? classes.overflow : ''

  return (
    <MUIDialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={onCancel}
      closeAfterTransition
      PaperProps={{ className: `${classes.dialog} ${paperClassName} ${className ?? ''}`, style }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        style={{ borderBottom: 'none', padding: '30px 35px', ...contentStyle }}
        className={`${classes.overflow} ${classes.dialogContent} ${contentClassName ?? ''}`}
      >
        <>
          {subtitle && ( // Solo se muestra si `subtitle` está definido
            <Typography variant="subtitle1" className={classes.subtitle}>
              {subtitle}
            </Typography>
          )}
          {children}
          {isLoading && (
            <Box mt={4}>
              <LinearProgress />
            </Box>
          )}
        </>
      </DialogContent>

      {showActions && (
        <DialogActions
          className={classes.actionsContainer}
          style={{ padding: '0px 35px 30px 35px' }}
        >
          <Button
            onClick={onCancel}
            className={classes.actionsButton}
            style={{
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 400,
              fontFamily: 'Arial',
              lineHeight: '18.4px',
              letterSpacing: 0,
              padding: '16px 45px',
            }}
          >
            {cancelButtonText ?? (readOnly ? 'Cerrar' : 'Cancelar')}
          </Button>
          {!readOnly && (
            <Button
              onClick={onAccept}
              variant="contained"
              color="primary"
              className={classes.actionsButton}
              style={{
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: 400,
                fontFamily: 'Arial',
                lineHeight: '18.4px',
                letterSpacing: 0,
                padding: '16px 45px',
              }}
              autoFocus
              {...okButtonProps}
            >
              {okButtonText ?? 'Aceptar'}
            </Button>
          )}
        </DialogActions>
      )}
    </MUIDialog>
  )
}

Dialog.defaultProps = {
  subtitle: '',
  contentStyle: {},
  style: {},
  className: '',
  contentClassName: '',
  showActions: false,
  okButtonText: '',
  okButtonProps: {},
  cancelButtonText: '',
  isLoading: false,
  overflow: false,
  readOnly: false,
  onAccept: () => undefined,
}

export { Dialog }
